// src/providers/AuthProvider.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import apiService from "../services/apiService";
import { getTokenExpiryMs } from "../utils/jwt";

const AuthContext = createContext();

const REFRESH_SAFETY_MARGIN_MS = 5 * 60 * 1000; // refresh 5 min before real expiry
const FALLBACK_REFRESH_INTERVAL_MS = 45 * 60 * 1000; // used if exp claim can't be read
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000; // never schedule a refresh sooner than this

const isDev = process.env.NODE_ENV !== "production";

export const AuthProvider = ({ children }) => {
  // Requires AuthProvider to be rendered INSIDE <MsalProvider> for useMsal() to work.
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const isLoggingOutRef = useRef(false);

  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    setLoading(true);

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Forget this app's cached MSAL token locally FIRST, before wiping
    // storage below — this does NOT sign the user out of Microsoft/AAD
    // globally, it only prevents this browser from silently reusing a
    // stale account/token on the next login.
    try {
      const account = accounts[0] || instance.getAllAccounts()[0];
      if (account && instance.clearCache) {
        await instance.clearCache({ account });
      }
    } catch (error) {
      if (isDev) console.error("MSAL cache clear error:", error);
    }

    try {
      // apiService.logout() calls the backend /logout, then internally
      // wipes localStorage + sessionStorage completely (see clearAuth()).
      await apiService.logout();
    } catch (error) {
      if (isDev) console.error("Logout error:", error);
    }

    setUser(null);
    setLoading(false);
    window.location.href = "/login";
  }, [instance, accounts]);

  // Schedules the next proactive refresh based on the token's real `exp`
  // claim instead of a guessed fixed interval.
  const scheduleNextRefresh = useCallback((idToken) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const expiryMs = getTokenExpiryMs(idToken);
    let delay = FALLBACK_REFRESH_INTERVAL_MS;
    if (expiryMs) {
      delay = Math.max(expiryMs - Date.now() - REFRESH_SAFETY_MARGIN_MS, MIN_REFRESH_DELAY_MS);
    }

    refreshTimerRef.current = setTimeout(() => {
      silentlyRefreshTokenRef.current?.();
    }, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const silentlyRefreshToken = useCallback(async () => {
    const account = accounts[0] || instance.getAllAccounts()[0];
    if (!account) return false;

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        account,
        scopes: ["openid", "profile", "email"],
      });
      if (tokenResponse?.idToken) {
        apiService.updateToken(tokenResponse.idToken);
        scheduleNextRefresh(tokenResponse.idToken);
        return true;
      }
    } catch (error) {
      if (isDev) console.error("Silent token refresh failed:", error);
      if (error instanceof InteractionRequiredAuthError) {
        // Refresh token itself is dead (expired/revoked consent) —
        // nothing left to try silently.
        await logout();
      }
    }
    return false;
  }, [instance, accounts, logout, scheduleNextRefresh]);

  // Kept in a ref so scheduleNextRefresh's setTimeout always calls the
  // latest version without needing to be redeclared on every render.
  const silentlyRefreshTokenRef = useRef(silentlyRefreshToken);
  useEffect(() => {
    silentlyRefreshTokenRef.current = silentlyRefreshToken;
  }, [silentlyRefreshToken]);

  // Let apiService self-heal a 401 via a silent refresh + one retry.
  useEffect(() => {
    apiService.setRefreshHandler(silentlyRefreshToken);
  }, [silentlyRefreshToken]);

  // Initial auth restore on app load.
  useEffect(() => {
    const initAuth = () => {
      try {
        const token = apiService.getToken();
        const storedUser = localStorage.getItem("user");

        if (token && storedUser) {
          if (apiService.isSessionExpired()) {
            apiService.clearAuth();
            return;
          }
          try {
            setUser(JSON.parse(storedUser));
            scheduleNextRefresh(token);
          } catch (e) {
            if (isDev) console.error("Error parsing stored user:", e);
            apiService.clearAuth();
          }
        }
      } catch (error) {
        if (isDev) console.error("Auth initialization error:", error);
        apiService.clearAuth();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enforce the fixed 16h session cap, independent of how many token
  // refreshes happened in between.
  useEffect(() => {
    if (!user) return;
    const intervalId = setInterval(() => {
      if (apiService.isSessionExpired()) logout();
    }, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [user, logout]);

  // Cross-tab sync: if another tab logs out (clears auth_token), follow it
  // here instead of silently keeping this tab authenticated.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "auth_token" && e.newValue === null && user) {
        setUser(null);
        window.location.href = "/login";
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user]);

  const login = useCallback(
    (userData) => {
      isLoggingOutRef.current = false;
      setUser(userData);
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
        const token = apiService.getToken();
        if (token) scheduleNextRefresh(token);
      }
    },
    [scheduleNextRefresh]
  );

  const value = {
    user,
    setUser,
    login,
    logout,
    loading,
    refreshToken: silentlyRefreshToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export default AuthProvider;